/**
 * Extracts unique colors from an image
 * @param imageUrl - URL of the image to analyze
 * @param colorTolerance - Tolerance for grouping similar colors (0-255, default: 30)
 * @param maxColors - Maximum number of colors to return (default: 20)
 * @returns Promise resolving to array of unique colors with hex codes
 */

export interface ExtractedColor {
  hex: string
  rgb: { r: number; g: number; b: number }
  count: number // Number of pixels with this color
}

export const extractImageColors = async (
  imageUrl: string,
  colorTolerance: number = 30,
  maxColors: number = 20
): Promise<ExtractedColor[]> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    
    // Try to set crossOrigin for CORS, but handle cases where it might fail
    try {
      img.crossOrigin = 'anonymous'
    } catch (e) {
      // Some images might not support CORS
      console.warn('Could not set crossOrigin:', e)
    }

    img.onload = () => {
      try {
        // Create canvas and draw image
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'))
          return
        }

        // Set canvas size to image size
        canvas.width = img.width
        canvas.height = img.height

        // Draw image to canvas
        ctx.drawImage(img, 0, 0)

        // Get image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const data = imageData.data

        // Extract colors and group similar ones with averaging
        // Each color group stores: total R, total G, total B, and count for weighted averaging
        const colorMap = new Map<
          string,
          { totalR: number; totalG: number; totalB: number; count: number }
        >()

        // Process pixels (sample every nth pixel for performance on large images)
        const sampleRate = Math.max(1, Math.floor((img.width * img.height) / 10000))

        for (let i = 0; i < data.length; i += 4 * sampleRate) {
          const r = data[i]
          const g = data[i + 1]
          const b = data[i + 2]
          const a = data[i + 3]

          // Skip transparent pixels
          if (a < 128) continue

          // Find similar color group or create new one
          const colorKey = findSimilarColorKey(colorMap, r, g, b, colorTolerance)

          if (colorKey) {
            // Add to existing group - accumulate for averaging
            const existing = colorMap.get(colorKey)!
            existing.totalR += r * sampleRate
            existing.totalG += g * sampleRate
            existing.totalB += b * sampleRate
            existing.count += sampleRate
          } else {
            // Create new color group
            colorMap.set(`${r},${g},${b}`, {
              totalR: r * sampleRate,
              totalG: g * sampleRate,
              totalB: b * sampleRate,
              count: sampleRate,
            })
          }
        }

        // Convert to array, calculate averages, and sort by count (most frequent first)
        const colors: ExtractedColor[] = Array.from(colorMap.entries())
          .map(([_, color]) => {
            // Calculate weighted average RGB
            const avgR = Math.round(color.totalR / color.count)
            const avgG = Math.round(color.totalG / color.count)
            const avgB = Math.round(color.totalB / color.count)

            return {
              hex: rgbToHex(avgR, avgG, avgB),
              rgb: { r: avgR, g: avgG, b: avgB },
              count: color.count,
            }
          })
          .sort((a, b) => b.count - a.count)
          .slice(0, maxColors)

        resolve(colors)
      } catch (error) {
        reject(error)
      }
    }

    img.onerror = () => {
      reject(new Error('Failed to load image for color extraction. CORS may be blocking the image.'))
    }

    img.src = imageUrl
  })
}

/**
 * Finds a similar color key in the map within tolerance
 * Uses the average RGB values from the color group for comparison
 */
function findSimilarColorKey(
  colorMap: Map<
    string,
    { totalR: number; totalG: number; totalB: number; count: number }
  >,
  r: number,
  g: number,
  b: number,
  tolerance: number
): string | null {
  for (const [key, color] of colorMap.entries()) {
    // Calculate average RGB for this color group
    const avgR = color.totalR / color.count
    const avgG = color.totalG / color.count
    const avgB = color.totalB / color.count

    // Check if the new color is similar to the average
    const dr = Math.abs(avgR - r)
    const dg = Math.abs(avgG - g)
    const db = Math.abs(avgB - b)

    if (dr <= tolerance && dg <= tolerance && db <= tolerance) {
      return key
    }
  }
  return null
}

/**
 * Converts RGB values to hex string
 */
function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map((x) => {
    const hex = x.toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }).join('')}`
}

